import { CardDisclosureTrigger } from '@/components/patterns/card-disclosure'
import { CashDrawerReceiptLines } from '@/components/pos/CashDrawerReceipt'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Card } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { calculateCashDifferenceCents } from '@/lib/services/cash-drawers/cash-drawers.reconciliation'
import { formatCurrency } from '@/lib/utils'
import { useLocalStorage } from '@uidotdev/usehooks'
import { ChevronDown } from 'lucide-react'

// Per-browser preference, open by default. v2: the hook stores its default on first render, so
// browsers that saw the old collapsed default would otherwise keep it without having chosen it.
export const EXPECTED_DETAILS_STORAGE_KEY = 'onestem.cash-drawer.expected-details-open.v2'

/**
 * Stays JavaScript for now: it composes `patterns/card-disclosure.jsx`, which is untyped, so this
 * types the seam instead of converting.
 * @param {{
 *   lines: import('@/lib/services/cash-drawers/cash-drawers.types').CashDrawerReceiptLine[],
 *   expectedCash: number | string | null,
 * }} props
 */
export function CashDrawerExpectedDisclosure({ lines, expectedCash }) {
  const [open, setOpen] = useLocalStorage(EXPECTED_DETAILS_STORAGE_KEY, true)

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <Card surface="elevated" size="flush" className="@container overflow-hidden">
        <CollapsibleTrigger asChild>
          <CardDisclosureTrigger className="group flex items-center justify-between gap-4 px-4 py-3">
            <span className="min-w-0 flex-1">
              <Typography as="span" variant="labelStrong" className="block">
                What the register expected
              </Typography>
              <Typography as="span" variant="caption" className="block">
                Starting cash + cash received − cash paid out
              </Typography>
            </span>
            <span className="flex shrink-0 items-center gap-3">
              <Typography as="span" variant="h4" className="tabular-nums">
                {formatCurrency(expectedCash)}
              </Typography>
              <ChevronDown className="size-4 transition-transform group-data-[state=open]:rotate-180" />
            </span>
          </CardDisclosureTrigger>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t p-4">
            <CashDrawerReceiptLines
              role="region"
              aria-label="What the register expected"
              lines={lines.filter((line) => line.key !== 'register_expected')}
            />
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

/** @param {{ counted: number | string, expected: number | string, label: string }} props */
export function CashDrawerDifference({ counted, expected, label }) {
  if (counted === '') return null

  const differenceCents = calculateCashDifferenceCents(counted, expected)
  const differenceLabel =
    differenceCents === 0
      ? 'Balanced'
      : `${differenceCents > 0 ? 'Over' : 'Short'} ${formatCurrency(Math.abs(differenceCents) / 100)}`

  return (
    <div
      aria-live="polite"
      className="flex items-center justify-between gap-3 rounded-md bg-muted/30 px-3 py-2"
    >
      <Typography variant="label">{label}</Typography>
      <Typography
        variant="labelStrong"
        tone={differenceCents === 0 ? 'success' : differenceCents > 0 ? 'warning' : 'destructive'}
        className="tabular-nums"
      >
        {differenceLabel}
      </Typography>
    </div>
  )
}
